import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { LogOut } from "lucide-react";
import { useAuth } from "@/features/auth/model/AuthContext";
import { WithPermission } from "../auth/WithPermission";
import { Permission } from "@/shared/types/roles";
import { LanguageSelector } from "@/shared/widgets/language-selector";
import { useTranslation } from '@/shared/i18n';

export const TopBar = () => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, logout } = useAuth();

  const handleLogout = async () => {
    try {
      logout();

      navigate("/auth");
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('logoutError'),
        description: error.message,
      });
    }
  };

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="bg-[#1A1A1A] flex w-full max-w-[1920px] items-center justify-center px-8 h-16 border-b border-[rgba(255,255,255,0.06)] max-md:px-5 fixed top-0 left-0 right-0 z-50">
      <div className="flex w-[1136px] max-w-full items-center justify-between">
        <div className="flex items-center gap-1 text-white">
          <Link to="/">
            <img
              loading="eager"
              src="/lovable-uploads/9fa11231-25ce-4026-9b1f-834785684e58.png"
              className="h-6"
              alt="LikeAPPro Logo"
            />
          </Link>
        </div>

        <nav className="flex items-center gap-12 text-sm">

          <WithPermission permissions={[Permission.PACKS_VIEW]}>
            <Link
              to="/"
              className={
                isCurrentPath("/")
                  ? "text-[#C1174F]"
                  : "text-white/60 hover:text-white transition-colors"
              }
            >
              {t('home')}
            </Link>
          </WithPermission>
          <WithPermission permissions={[Permission.ANALYTICS_VIEW]}>
            <Link
              to="/stats"
              className={
                isCurrentPath("/stats")
                  ? "text-[#C1174F]"
                  : "text-white/60 hover:text-white transition-colors"
              }
            >
              {t('statistics')}
            </Link>
          </WithPermission>
          <WithPermission permissions={[Permission.USERS_VIEW]}>
            <Link
              to="/users"
              className={
                isCurrentPath("/users")
                  ? "text-[#C1174F]"
                  : "text-white/60 hover:text-white transition-colors"
              }
            >
              {t('users')}
            </Link>
          </WithPermission>
          <WithPermission permissions={[Permission.TAGS_VIEW]}>
            <Link
              to="/tags"
              className={
                isCurrentPath("/tags")
                  ? "text-[#C1174F]"
                  : "text-white/60 hover:text-white transition-colors"
              }
            >
              {t('tags')}
            </Link>
          </WithPermission>
          <WithPermission permissions={[Permission.LEARNING_VIEW]}>
            <Link
              to="/learning"
              className={
                isCurrentPath("/learning")
                  ? "text-[#C1174F]"
                  : "text-white/60 hover:text-white transition-colors"
              }
            >
              {t('learnings')}
            </Link>
          </WithPermission>
          <WithPermission permissions={[Permission.CATEGORIES_VIEW]}>
            <Link
              to="/categories"
              className={
                location.pathname.startsWith("/categories")
                  ? "text-[#C1174F]"
                  : "text-white/60 hover:text-white transition-colors"
              }
            >
              {t('categories')}
            </Link>
          </WithPermission>
        </nav>

        <div className="flex items-center gap-2">
          {isAuthenticated && <LanguageSelector />}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-white text-gray-800 hover:bg-white/90"
                >
                  {t('menu')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 bg-white text-gray-800 border border-gray-200"
              >
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer hover:bg-[#C1174F] hover:text-white transition-colors"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => navigate("/auth")}>{t('signIn')}</Button>
          )}
        </div>
      </div>
    </header>
  );
};
